import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  CRow,
  CCol,
  CButton,
  CSpinner,
  CFormCheck,
  CFormLabel,
  CFormSelect,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilCloudDownload, cilReload, cilTrash, cilCalendar } from "@coreui/icons";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";

registerLocale("ru", ru);

const CameraFilterPanel = ({
  t,
  isStaff,
  isSuperuser,
  triggerExportExcel,
  exportingExcel,
  setRecalcStartDate,
  setRecalcEndDate,
  startDate,
  setRecalcModalVisible,
  setFormatConfirmation,
  setFormatModalVisible,
  toast,
  filterMode,
  setFilterMode,
  datePreset,
  handlePresetChange,
  setStartDate,
  endDate,
  setEndDate,
  availableDates,
  highlightedDates = [],
  currentLang = "ru",
  baseDate,
  setBaseDate,
  selectedShiftId,
  setSelectedShiftId,
  shiftOptions,
  fetchAnalytics,
  loadingAnalytics,
  device,
  analyticsData,
  analyticsCooldown,
  volumeMode,
  setVolumeMode,
  variant = "excavator",
  hideVolumeMode = false,
  hideShiftMode = false,
  openFormatModal,
}) => {
  const isTruck = variant === "truck";

  const [syncingOmni, setSyncingOmni] = useState(false);
  const [omniCooldown, setOmniCooldown] = useState(0);

  useEffect(() => {
    if (omniCooldown > 0) {
      const timer = setTimeout(() => setOmniCooldown((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [omniCooldown]);

  const handleOmnicommSync = async () => {
    if (!startDate || !endDate) {
      toast.warn("Пожалуйста, выберите даты начала и окончания.");
      return;
    }
    if (omniCooldown > 0) return;

    setSyncingOmni(true);
    try {
      const token = localStorage.getItem("token");
      const devId = window.location.pathname.split("/").pop();

      const startIso = startDate.toISOString();
      const endIso = endDate.toISOString();

      const res = await axios.post(
        "/api/analytics/omnicomm-sync/",
        {
          device_id: devId,
          start_date: startIso,
          end_date: endIso,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(res.data.message || "Синхронизация успешно выполнена!");

      if (fetchAnalytics) {
        fetchAnalytics();
      }
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка синхронизации с Omnicomm");
    } finally {
      setSyncingOmni(false);
      if (!isSuperuser) setOmniCooldown(10);
    }
  };

  const isRussian = currentLang === "ru" || currentLang?.startsWith?.("ru");
  const datePickerLocale = isRussian ? "ru" : (currentLang === "en" ? "en-US" : currentLang);

  // Формируем структуру подсветки дней с погрузками для react-datepicker
  const highlightWithRanges = useMemo(() => {
    if (!highlightedDates || highlightedDates.length === 0) return undefined;
    return [
      {
        "react-datepicker__day--highlighted-loading": highlightedDates,
      },
    ];
  }, [highlightedDates]);

  return (
    <div className="w-100">
      <div className="mb-3 d-flex justify-content-between align-items-center border-bottom pb-3 flex-wrap gap-3">
        {!hideShiftMode ? (
          <div className="d-flex gap-4">
            <CFormCheck
              type="radio"
              name="filterMode"
              id="modeCustom"
              label={
                <span className="fw-bold text-dark">
                  {t("analysis.custom_range", "Произвольный период")}
                </span>
              }
              checked={filterMode === "custom"}
              onChange={() => setFilterMode("custom")}
            />
            <CFormCheck
              type="radio"
              name="filterMode"
              id="modeShift"
              label={
                <span className="fw-bold text-dark">
                  {t("analysis.by_shift", "По сменам")}
                </span>
              }
              checked={filterMode === "shift"}
              onChange={() => setFilterMode("shift")}
            />
          </div>
        ) : (
          <div />
        )}

        <div className="d-flex flex-wrap gap-2 align-items-center">
          {!isTruck && isStaff && device && device.omnicomm_id && device.has_omnicomm_config && (
            <CButton
              className="fw-semibold btn-clean-action btn-mobile-100"
              onClick={handleOmnicommSync}
              disabled={syncingOmni || !startDate || !endDate || (!isSuperuser && omniCooldown > 0)}
            >
              {syncingOmni ? <CSpinner size="sm" className="me-2" /> : <span className="me-2">📡</span>}
              {omniCooldown > 0 ? `Ожидайте (${omniCooldown}с)` : "Найти машины (Omnicomm)"}
            </CButton>
          )}

          {triggerExportExcel && (
            <CButton
              className="fw-semibold btn-clean-action btn-mobile-100"
              onClick={triggerExportExcel}
              disabled={exportingExcel}
            >
              {exportingExcel ? (
                <CSpinner size="sm" className="me-2" />
              ) : (
                <CIcon icon={cilCloudDownload} className="me-2" />
              )}
              {t("common.export_excel", "Экспорт Excel")}
            </CButton>
          )}

          {isStaff && (
            <>
              <CButton
                className="fw-semibold btn-clean-action btn-mobile-100"
                onClick={() => {
                  setRecalcStartDate(startDate || new Date());
                  if (setRecalcEndDate) setRecalcEndDate(endDate || new Date());
                  setRecalcModalVisible(true);
                }}
              >
                <CIcon icon={cilReload} className="me-2" />
                {t("camera.recalc_all", "Пересчитать")}
              </CButton>

              <CButton
                className="fw-semibold btn-clean-action btn-mobile-100 text-danger"
                onClick={() => {
                  if (openFormatModal) {
                    openFormatModal();
                  } else {
                    setFormatConfirmation("");
                    setFormatModalVisible(true);
                  }
                }}
              >
                <CIcon icon={cilTrash} className="me-2" />
                {t("camera_modals.btn_format_main", "Очистить")}
              </CButton>
            </>
          )}
        </div>
      </div>

      {!hideVolumeMode && (
        <div className="mb-3 d-flex align-items-center gap-3">
          <label className="text-muted small fw-bold mb-0 text-nowrap text-uppercase">
            Методика расчета объема:
          </label>
          <CFormSelect
            className="fw-bold bg-white text-primary border-primary shadow-sm"
            style={{ maxWidth: "300px", cursor: "pointer" }}
            value={volumeMode}
            onChange={(e) => setVolumeMode(e.target.value)}
          >
            <option value="nominal">Номинальный объем</option>
            <option value="solid">Объем в твердом теле</option>
            {(device?.use_digital_twin || analyticsData?.use_digital_twin) && (
              <>
                <option value="twin">Цифровой двойник AI</option>
                <option value="twin_solid">Твердое тело (AI Двойник)</option>
              </>
            )}
          </CFormSelect>
        </div>
      )}

      <div className="mt-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div className="d-flex align-items-center">
            <CIcon icon={cilCalendar} className="text-primary me-2" size="lg" />
            <span className="fw-bold text-dark fs-6">Настройка периода</span>
          </div>

          {highlightedDates && highlightedDates.length > 0 && (
            <div className="small text-muted d-flex align-items-center gap-2">
              <span
                style={{
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  backgroundColor: "#ff5722",
                  border: "2px solid #bf360c",
                }}
              />
              <span>— в календаре отмечены дни с погрузками ({highlightedDates.length} дн.)</span>
            </div>
          )}
        </div>

        {filterMode === "custom" ? (
          <CRow className="align-items-end g-3">
            <CCol xs={12} lg={3} md={6}>
              <CFormLabel className="text-muted small fw-bold mb-1 text-uppercase" style={{ letterSpacing: "0.5px" }}>
                {t("analysis.preset_select", "Шаблон периода")}
              </CFormLabel>
              <CFormSelect
                value={datePreset}
                onChange={(e) => handlePresetChange(e.target.value)}
                className="fw-semibold"
                style={{ cursor: "pointer" }}
              >
                <option value="custom">{t("analysis.preset_custom", "Произвольный")}</option>
                <option value="today">{t("analysis.preset_today", "Сегодня (незаверш.)")}</option>
                <option value="yesterday">{t("analysis.preset_yesterday", "Вчера (00:00 - 23:59)")}</option>
                <option value="last_2_days">{t("analysis.preset_2_days", "Последние 2 дня")}</option>
                <option value="last_3_days">{t("analysis.preset_3_days", "Последние 3 дня")}</option>
                <option value="last_7_days">{t("analysis.preset_7_days", "Последние 7 дней")}</option>
                <option value="last_14_days">{t("analysis.preset_14_days", "Последние 14 дней")}</option>
                <option value="last_month">{t("analysis.preset_month", "Месяц (последние 30 дней)")}</option>
              </CFormSelect>
            </CCol>

            <CCol xs={12} lg={3} md={6}>
              <CFormLabel className="text-muted small fw-bold mb-1 text-uppercase" style={{ letterSpacing: "0.5px" }}>
                {t("common.date_start", "Дата начала")}
              </CFormLabel>
              <DatePicker
                selected={startDate}
                onChange={(d) => {
                  setStartDate(d);
                  handlePresetChange("custom");
                }}
                className="form-control fw-semibold w-100"
                wrapperClassName="w-100"
                showTimeSelect
                timeCaption="Время"
                dateFormat="dd.MM.yyyy HH:mm"
                locale={datePickerLocale}
                includeDates={availableDates && availableDates.length > 0 ? availableDates : undefined}
                highlightDates={highlightWithRanges}
                maxDate={endDate}
              />
            </CCol>

            <CCol xs={12} lg={3} md={6}>
              <CFormLabel className="text-muted small fw-bold mb-1 text-uppercase" style={{ letterSpacing: "0.5px" }}>
                {t("common.date_end", "Дата окончания")}
              </CFormLabel>
              <DatePicker
                selected={endDate}
                onChange={(d) => {
                  setEndDate(d);
                  handlePresetChange("custom");
                }}
                className="form-control fw-semibold w-100"
                wrapperClassName="w-100"
                showTimeSelect
                timeCaption="Время"
                dateFormat="dd.MM.yyyy HH:mm"
                locale={datePickerLocale}
                includeDates={availableDates && availableDates.length > 0 ? availableDates : undefined}
                highlightDates={highlightWithRanges}
                minDate={startDate}
                filterTime={(time) => {
                  if (startDate && time.toDateString() === startDate.toDateString()) {
                    return time.getTime() > startDate.getTime();
                  }
                  return true;
                }}
              />
            </CCol>

            <CCol xs={12} lg={3} md={6} className="mt-3 mt-lg-0">
              <CButton
                color="primary"
                onClick={fetchAnalytics}
                disabled={loadingAnalytics || (!isSuperuser && analyticsCooldown > 0)}
                className="w-100 fw-bold"
                style={{ height: "38px" }}
              >
                {loadingAnalytics ? (
                  <>
                    <CSpinner size="sm" className="me-2" />
                    Загрузка...
                  </>
                ) : !isSuperuser && analyticsCooldown > 0 ? (
                  `Ожидайте ${analyticsCooldown}с`
                ) : (
                  t("common.get_analytics", "Получить аналитику")
                )}
              </CButton>
            </CCol>
          </CRow>
        ) : (
          <CRow className="align-items-end g-3">
            <CCol xs={12} lg={4} md={6}>
              <CFormLabel className="text-muted small fw-bold mb-1 text-uppercase" style={{ letterSpacing: "0.5px" }}>
                {t("analysis.base_date", "Дата смены")}
              </CFormLabel>
              <DatePicker
                selected={baseDate}
                onChange={setBaseDate}
                className="form-control fw-semibold w-100"
                wrapperClassName="w-100"
                dateFormat="dd.MM.yyyy"
                locale={datePickerLocale}
                includeDates={availableDates && availableDates.length > 0 ? availableDates : undefined}
                highlightDates={highlightWithRanges}
              />
            </CCol>

            <CCol xs={12} lg={4} md={6}>
              <CFormLabel className="text-muted small fw-bold mb-1 text-uppercase" style={{ letterSpacing: "0.5px" }}>
                {t("analysis.select_shift", "Выберите смену")}
              </CFormLabel>
              <CFormSelect
                value={selectedShiftId}
                onChange={(e) => setSelectedShiftId(e.target.value)}
                className="fw-semibold"
                style={{ cursor: "pointer" }}
              >
                <option value="">-- {t("common.select", "Выберите")} --</option>
                {shiftOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.start.substring(0, 5)} - {s.end.substring(0, 5)})
                  </option>
                ))}
              </CFormSelect>
            </CCol>

            <CCol xs={12} lg={4} md={12} className="mt-3 mt-lg-0">
              <CButton
                color="primary"
                onClick={fetchAnalytics}
                disabled={loadingAnalytics || !selectedShiftId || (!isSuperuser && analyticsCooldown > 0)}
                className="w-100 fw-bold"
                style={{ height: "38px" }}
              >
                {loadingAnalytics ? (
                  <>
                    <CSpinner size="sm" className="me-2" />
                    Загрузка...
                  </>
                ) : !isSuperuser && analyticsCooldown > 0 ? (
                  `Ожидайте ${analyticsCooldown}с`
                ) : (
                  t("common.get_analytics", "Получить аналитику")
                )}
              </CButton>
            </CCol>
          </CRow>
        )}
      </div>
    </div>
  );
};

export default CameraFilterPanel;